/**
 * Hook de orquestração para salvar pré-nota.
 * Centraliza validação, configuração e lógica de negócio do formulário.
 *
 * @returns Funções e estados para controle do formulário de pré-nota
 */
"use client";
import { useMemo, useEffect, useState } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import { postPrenotaPayloadSchemaCondicional } from "@PN/inclusao";
import { useUser } from "store/_auth.hooks";
import { toast } from "sonner";

export function useSavePreNota() {
  const draft = usePrenotaStore((s) => s.draft);
  const aux = usePrenotaStore((s) => s.aux);
  const setUsuario = usePrenotaStore((s) => s.actions.draft.setUsuario);
  const reset = usePrenotaStore((s) => s.actions.reset);
  const user = useUser();
  const [isLocaleValid, setIsLocaleValid] = useState(true);

  // Função de reset simplificada
  const safeReset = () => {
    try {
      if (reset) {
        reset();
      }
    } catch (error) {}
  };

  // Validação do idioma do navegador
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const browserLanguage = navigator.language;
    const isValid = browserLanguage === 'pt-BR';
    
    setIsLocaleValid(isValid);
    
    if (!isValid) {
      toast.error('Idioma do navegador incompatível', {
        description: `Seu navegador está configurado para "${browserLanguage}". Por favor, altere o idioma do navegador para "pt-BR" (Português Brasil) para utilizar este sistema.`,
        duration: 10000,
      });
    }
  }, []);

  // Preenche automaticamente o usuário no draft, se estiver vazio
  useEffect(() => {
    if (!draft.usuario && user.login && user.login !== "Usuário Desconhecido") {
      setUsuario(user.login);
    }
  }, [draft.usuario, user.login, setUsuario]);

  // Validação do formulário
  const validationResult = useMemo(() => {
    return postPrenotaPayloadSchemaCondicional.safeParse(draft);
  }, [draft]);

  // Sem pedido de compra: espécie deve ser DOC, FAT ou CTE (usa aux.pedidos, igual ao useCardFlow)
  // Valores primitivos nas deps para reagir quando o usuário remove o pedido
  const pedidosLength = aux.pedidos?.length ?? 0;
  const primeiroCodPedido = aux.pedidos?.[0]?.codPedido ?? "";
  const temPedidoCompra = useMemo(
    () => !!(pedidosLength && primeiroCodPedido.trim() !== ""),
    [pedidosLength, primeiroCodPedido]
  );
  const especie = draft.documento?.especie ?? "";
  const podeSalvarEspecie =
    temPedidoCompra || especie === "DOC" || especie === "FAT" || especie === "CTE" || especie === "NFPS";
  const hasPedidoPendente = useMemo(() => {
    const pedidos = aux.pedidos || [];
    return pedidos.some((pedido) => {
      const status = String((pedido as any)?.status || "")
        .trim()
        .toLowerCase();
      return status.includes("pendente") || status.includes("aguardando");
    });
  }, [aux.pedidos]);
  const valorNF = useMemo(() => {
    if (
      aux.totalNota &&
      typeof aux.totalNota === "object" &&
      (aux.totalNota as any).total
    ) {
      const totalStr = String((aux.totalNota as any).total || "0");
      return Number(totalStr.replace(",", ".")) || 0;
    }
    return draft.produtos.reduce((sum, p) => sum + (p.valorTotal || 0), 0);
  }, [draft.produtos, aux.totalNota]);
  const totalParcelas = useMemo(
    () =>
      (draft.pagamentos?.parcelas || []).reduce(
        (sum, parcela) => sum + (parcela.valor || 0),
        0
      ),
    [draft.pagamentos?.parcelas]
  );
  const isParcelasTotalValido = useMemo(() => {
    return valorNF === 0 || Math.abs(totalParcelas - valorNF) < 0.01;
  }, [totalParcelas, valorNF]);

  const isFormValid =
    validationResult.success &&
    isLocaleValid &&
    podeSalvarEspecie &&
    isParcelasTotalValido &&
    !hasPedidoPendente;

  const invalidReasons = useMemo(() => {
    const reasons: string[] = [];

    if (!isLocaleValid) {
      reasons.push(
        "Idioma do navegador deve ser pt-BR (Português Brasil), Entre em configurações -> Idioma -> Português Brasil"
      );
    }

    if (hasPedidoPendente) {
      reasons.push(
        "Existe pedido de compra pendente/aguardando. Libere o pedido para habilitar o salvamento."
      );
    }

    if (validationResult.success && !podeSalvarEspecie) {
      reasons.push("Sem pedido de compra, a espécie deve ser DOC, FAT, CTE.");
    }

    if (validationResult.success && !isParcelasTotalValido) {
      reasons.push(
        "A soma das parcelas deve fechar exatamente o valor total da nota para salvar."
      );
    }

    if (!validationResult.success) {
      validationResult.error.errors.forEach((err) => {
        const path = err.path.join(".") || "Formulário";
        const message = `${path}: ${err.message}`;
        if (!reasons.includes(message)) {
          reasons.push(message);
        }
      });
    }

    return reasons;
  }, [
    isLocaleValid,
    hasPedidoPendente,
    validationResult.success,
    validationResult.error,
    podeSalvarEspecie,
    isParcelasTotalValido,
  ]);

  // Payload final formatado para exibição
  const formattedPayload = useMemo(() => {
    try {
      // Remove campos desnecessários para exibição
      const cleanPayload = {
        ...draft,
        _id: undefined,
        _version: undefined,
      };
      return JSON.stringify(cleanPayload, null, 2);
    } catch (error) {
      return "Erro ao formatar payload";
    }
  }, [draft]);

  // Mensagem de pendência para o hover
  const invalidReasonMessage = useMemo(() => {
    if (isFormValid) return undefined;
    return invalidReasons[0] || "Verifique o formulário para pendências.";
  }, [
    isFormValid,
    invalidReasons,
  ]);

  const duplicateValidationHint = useMemo(() => {
    const chave = draft.documento?.chaveNFe?.trim();
    if (!chave) return undefined;
    return "Ao salvar, a chave sera revalidada para bloquear duplicidade.";
  }, [draft.documento?.chaveNFe]);

  /**
   * Handler para salvar a pré-nota.
   * Só executa a mutation se o formulário estiver válido.
   * @param mutate Função de mutation (TanStack)
   */
  const handleSave = (mutate: () => void) => {
    if (!isFormValid) {
      // Apenas retorna, sem toast. O toast será tratado no hook de mutação.
      return;
    }
    mutate();
  };

  return {
    isFormValid,
    validationResult,
    formattedPayload,
    invalidReasons,
    invalidCount: invalidReasons.length,
    invalidReasonMessage,
    duplicateValidationHint,
    handleSave,
    reset: safeReset,
  };
}
